import Component from '../../js/_Component';

const CNAME = 'c-block-recipes-view';

type RecipeCategory = {
	id: string,
	name: string,
};

type RecipeSearchQuery = {
	page: number,
	search: string,
	category: string,
	sort: string,
	format: string,
};

const SORT_COLUMNS = [
	'id',
	'name',
	'category',
	'ishot'
];

export default class BlockRecipesView extends Component {

	private categories: RecipeCategory[];

	private resultContainer: HTMLElement;

	private lastSearchData: RecipeSearchQuery = {
		page: 1,
		search: null,
		category: null,
		sort: 'id',
		format: 'card',
	};

	private busyLocks = 0;

	constructor(element: HTMLElement) {
		super(element);

		this.resultContainer = element.querySelector(`.${CNAME}--result-section`);

		this.element.querySelector(`.${CNAME}--prev-page-button button`).addEventListener('click', () => this.prevPage());
		this.element.querySelector(`.${CNAME}--next-page-button button`).addEventListener('click', () => this.nextPage());
		this.element.querySelector(`.${CNAME}--search-button button`).addEventListener('click', () => this.newSearch());
		this.element.querySelector(`.${CNAME}--clear-button button`).addEventListener('click', () => this.clearFilters());
		this.element.querySelector(`.${CNAME}--card-format-button button`).addEventListener('click', () => this.switchFormat());
		this.element.querySelector(`.${CNAME}--table-format-button button`).addEventListener('click', () => this.switchFormat());
		this.element.querySelector(`.${CNAME}--controls`).addEventListener('keydown', (e: KeyboardEvent) => {
			if (e.key === 'Enter') {
				this.newSearch();
			}
		});

		// Add table sorting functionality.
		this.resultContainer.addEventListener('click', (e) => this.handleSortEvent(e));

		// Get search parameters from URL.
		let currentUrl = new URL(window.location.href);
		let rawParams = currentUrl.searchParams.get('s');
		let params = null;
		if (rawParams) {
			try {
				rawParams = atob(rawParams);
				params = JSON.parse(rawParams);
			} catch {}
		}

		// Retrieve available categories.
		this.setBusy();
		fetch(`/action-recipe-categories`, {
			headers: {
				'Accept': 'application/json',
			},
		}).then(results => results.json() as Promise<RecipeCategory[]>)
		.then(categories => {
			this.categories = categories;

			// Create input for eneral text search.
			let input = document.createElement('input');
			this.element.querySelector(`.${CNAME}--text-search`).appendChild(input);

			// Create input for category option.
			let select = document.createElement('select');
			let option = document.createElement('option');
			option.value = '';
			option.innerText = 'All';
			select.appendChild(option);
			categories.forEach((category) => {
				option = document.createElement('option');
				option.value = category.id.toString();
				option.innerText = category.name;
				select.appendChild(option);
			});
			this.element.querySelector(`.${CNAME}--category-search`).appendChild(select);

			this.unsetBusy();

			if (params) {
				// Set provided search.
				this.lastSearchData.search = params.search || this.lastSearchData.search;
				(this.element.querySelector(`.${CNAME}--text-search input`) as HTMLInputElement).value = this.lastSearchData.search;

				// Set selected category.
				this.lastSearchData.category = params.category || this.lastSearchData.category;
				if (this.lastSearchData.category) {
					(this.element.querySelector(`.${CNAME}--category-search select`) as HTMLSelectElement).value = this.lastSearchData.category;
				}

				this.lastSearchData.page = params.page || this.lastSearchData.page;

				this.lastSearchData.sort = params.sort || this.lastSearchData.sort;

				this.lastSearchData.format = params.format || this.lastSearchData.format;

				if (this.lastSearchData.format == 'table') {
					this.switchFormat();
				}
			}

			this.loadResults();
		});
	}

	prevPage() {
		if (this.busyLocks) {
			return;
		}

		this.lastSearchData.page -= 1;
		this.loadResults();
	}

	nextPage() {
		if (this.busyLocks) {
			return;
		}

		this.lastSearchData.page += 1;
		this.loadResults();
	}

	newSearch() {
		this.lastSearchData.page = 1;

		// Get general text search (if one exists).
		this.lastSearchData.search = (this.element.querySelector(`.${CNAME}--text-search input`) as HTMLInputElement).value || null;

		// Get category option (if one is selected).
		this.lastSearchData.category = (this.element.querySelector(`.${CNAME}--category-search select`) as HTMLSelectElement).value || null;

		this.loadResults();
	}

	clearFilters() {
		(this.element.querySelector(`.${CNAME}--text-search input`) as HTMLInputElement).value = '';
		(this.element.querySelector(`.${CNAME}--category-search select`) as HTMLSelectElement).selectedIndex = 0;

		this.newSearch();
	}

	switchFormat() {
		if (this.element.classList.contains('m-card-mode')) {
			this.element.classList.remove('m-card-mode');
			this.element.classList.add('m-table-mode');
			this.lastSearchData.format = 'table';
		} else {
			this.element.classList.add('m-card-mode');
			this.element.classList.remove('m-table-mode');
			this.lastSearchData.format = 'card';
		}
		this.loadResults();
	}

	reportSearchError() {
		this.unsetBusy();
	}

	updateResults(results: string) {
		this.unsetBusy();

		// Find and replace all category IDs with their names. This could be done more explicitly by wrapping or
		// designating IDs somehow, but since they're UUIDs, and wrapping them would take more rearchitecting, it's
		// easier to just do this.
		this.categories.forEach((category) => {
			results = results.replace(new RegExp(category.id, 'g'), category.name);
		});

		this.resultContainer.innerHTML = results;

		// Add the sorting indicator to the new table (if it exists.
		let sortIndex = SORT_COLUMNS.indexOf(this.lastSearchData.sort.split('-')[0]) || 0;
		let sortHeading = this.resultContainer.querySelectorAll('th')[sortIndex];
		if (sortHeading) {
			// If this sorting mode is descending or not.
			if (this.lastSearchData.sort.indexOf('-desc') != -1) {
				sortHeading.classList.add('m-sort-desc');
			} else {
				sortHeading.classList.add('m-sort');
			}
		}

		// Hide/show the previous page button.
		if (this.lastSearchData.page == 1) {
			this.element.classList.add('m-first-page');
		} else {
			this.element.classList.remove('m-first-page');
		}

		// Hide/show the next page button.
		if ((this.resultContainer.lastElementChild as HTMLElement).dataset.lastPage !== undefined) {
			this.element.classList.add('m-last-page');
		} else {
			this.element.classList.remove('m-last-page');
		}
	}

	loadResults() {
		if (this.busyLocks) {
			return;
		}

		let fullUrl = new URL(window.location.href);
		fullUrl.searchParams.set('s', btoa(JSON.stringify(this.lastSearchData)));
		history.replaceState({}, '', fullUrl.href);

		this.setBusy();
		fetch(`/action-search-recipes?${this.searchParametersToURI(this.lastSearchData)}`, {
			headers: {
				'Accept': 'text/html',
			},
		}).then(results => results.text())
			.then(r => this.updateResults(r))
			.catch(() => this.reportSearchError());
	}

	searchParametersToURI(params) {
		let elements = [];
		for (let key in params) {
			let val = params[key];
			elements.push(`${key}=${encodeURIComponent(val || '')}`);
		}
		return elements.join('&');
	}

	private handleSortEvent(e: MouseEvent) {
		let currentHeading = (e.target as HTMLElement).closest('th');
		if (!currentHeading) {
			return;
		}

		this.resultContainer.querySelectorAll('th').forEach(heading => {
			heading.classList.remove('m-sort');
			heading.classList.remove('m-sort-desc');
		});

		let headingIndex = Array.from(currentHeading.parentElement.children).indexOf(currentHeading);
		let sortmode = SORT_COLUMNS[headingIndex] || 'id';
		if (this.lastSearchData.sort != sortmode) { // New sort, ascending.
			currentHeading.classList.add('m-sort');
			this.lastSearchData.sort = sortmode;
		} else { // Same sort, switch to descending.
			currentHeading.classList.add('m-sort-desc');
			this.lastSearchData.sort = `${sortmode}-desc`;
		}

		this.loadResults();
	}

	// Increments the busy counter, locking interaction.
	private setBusy() {
		this.busyLocks += 1;
		this.element.classList.add('m-busy');
	}

	// Decrements the busy counter, possibly unlocking interaction.
	private unsetBusy() {
		this.busyLocks -= 1;
		if (!this.busyLocks) {
			this.element.classList.remove('m-busy');
		}
	}
}

Component.register(BlockRecipesView, 'BlockRecipesView');
